import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  getCartSessionData,
  CartSessionData,
  convertSessionDataToCartModel
} from '../utils/sessionStorage';
import { partnersApi } from '../api/partners';
import { ApiError } from '../types';
import { DEFAULT_LOGO, DEFAULT_VIDEO_URL } from '../constants';

// Declare global window properties for embedded data
declare global {
  interface Window {
    __CART_MODEL_DATA__?: any;
    __PARTNER_ID__?: string;
  }
}

interface PartnerConfig {
  id: string;
  name: string;
  displayName: string;
  logo: string;
  favicon: string;
  isVrboPartner?: boolean;
  image?: string;
  videoUrl?: string;
  marketingText?: string;
  pages?: {
    landing?: {
      partnerIntegration?: {
        logo?: string;
        logoStyle?: string | null;
        partnerLogo?: string;
        integrationText?: string;
        videoUrl?: string | null;
        trustCount?: string;
        description?: string;
      };
    };
  };
  // Processed fields for easy access
  displayLogo?: string;
  displayVideoUrl?: string;
  displayMarketingText?: string;
  enableRegistrationOnlyProduct?: boolean;
}

interface CartModel {
  partnerId: string | number;
  partnerName: string;
  partnerImage: string | null;
  partnerImageExists: boolean;
  propertyCount?: number;
  subscriptionFeeTotal?: number;
  licenseFeeTotal?: number;
  total?: number;
  subscriptionLabel?: string;
  productPricing?: Array<{
    ProductId: number;
    ProductTypeId: number;
    LineItems: Array<{
      LineItemId: number;
      LineItemTypeId: number;
      Amount: number;
      Description: string;
    }>;
  }>;
}

interface PartnerContextType {
  partnerConfig: PartnerConfig | null;
  cartModel: CartModel | null;
  loading: boolean;
  error: string | null;
  partnerId: string | null;
  initializeFromSession: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const usePartner = () => {
  const context = useContext(PartnerContext);
  if (context === undefined) {
    throw new Error('usePartner must be used within a PartnerProvider');
  }
  return context;
};

interface PartnerProviderProps {
  children: React.ReactNode;
}

const processPartnerConfig = (partnerConfig: any) => ({
  ...partnerConfig,
  displayLogo: partnerConfig?.image || partnerConfig?.logo || DEFAULT_LOGO,
  usingDefaultLogo: !(partnerConfig?.image || partnerConfig?.logo),
  displayVideoUrl: partnerConfig?.videoUrl || partnerConfig?.pages?.landing?.partnerIntegration?.videoUrl || DEFAULT_VIDEO_URL,
  displayMarketingText: partnerConfig?.marketingText || partnerConfig?.pages?.landing?.partnerIntegration?.description
});

export const PartnerProvider: React.FC<PartnerProviderProps> = ({ children }) => {
  const [partnerConfig, setPartnerConfig] = useState<PartnerConfig | null>(null);
  const [cartModel, setCartModel] = useState<CartModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  
  // Request deduplication cache
  const requestCache = React.useRef<Map<string, Promise<any>>>(new Map());

  const fetchPartnerConfig = useCallback(async (id: string) => {
    try {
      // Validate input
      if (!id || typeof id !== 'string' || id.trim() === '') {
        console.error('Invalid partner ID provided:', id);
        throw new Error('Invalid partner ID');
      }

      // Check if we have a pending request for this partnerId
      if (requestCache.current.has(id)) {
        const result = await requestCache.current.get(id);
        setPartnerConfig(processPartnerConfig(result));
        setPartnerId(id);
        return;
      }

      setLoading(true);
      setError(null);

      // Create the request promise and cache it
      const requestPromise = partnersApi.getPartnerConfig(id);
      requestCache.current.set(id, requestPromise);

      try {
        const partnerConfig = await requestPromise;
        setPartnerConfig(processPartnerConfig(partnerConfig));
        setPartnerId(id);
      } finally {
        // Remove from cache after completion
        requestCache.current.delete(id);
      }
    } catch (err) {
      console.error('Error fetching partner config for:', id, err);
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to load partner configuration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCartModel = useCallback(async (id: string) => {
    try {
      // Simple: Get model data from sessionStorage (already set by LandingPage)
      const storedModelData = getCartSessionData();
      if (storedModelData) {
        // Convert CartSessionData to CartModel format
        const cartModelData: CartModel = convertSessionDataToCartModel(storedModelData);
        setCartModel(cartModelData);
      } else {
        // This should not happen - LandingPage should have set the session data
        console.error('No cart model data found in session - LandingPage should have set this');
        setError('Cart data not found. Please return to the cart and try again.');
      }
    } catch (err) {
      console.error('Error processing cart model data:', err);
      setError('Failed to process cart data. Please try again.');
    }
  }, []);

  // Method to initialize data from session (called by LandingPage after setting session)
  const initializeFromSession = useCallback(async () => {
    const sessionData = getCartSessionData();
    
    if (sessionData && sessionData.partnerId) {
      const partnerId = sessionData.partnerId.toString();
      
      // Fetch partner config if not already loaded (request cache prevents duplicate calls)
      if (!partnerConfig) {
        await fetchPartnerConfig(partnerId);
      }
      
      await fetchCartModel(partnerId);
    } else {
      console.error('No partner ID found in session - invalid cart state');
      setError('Invalid cart session. Please return to the cart and try again.');
      setLoading(false);
    }
  }, [fetchPartnerConfig, fetchCartModel, partnerConfig]);

  const value: PartnerContextType = {
    partnerConfig,
    cartModel,
    loading,
    error,
    partnerId,
    initializeFromSession
  };

  return (
    <PartnerContext.Provider value={value}>
      {children}
    </PartnerContext.Provider>
  );
};